<script lang="ts">
	import type { PageProps } from "./$types";
	import { YouTubeSection, PodcastSection } from "#lib/web-ui/blocks/index.js";
	import { BlogSection } from "#lib/web-ui/blocks/blog/index.js";
	import ArrowRightIcon from "@lucide/svelte/icons/arrow-right";

	let { data }: PageProps = $props();
</script>

<svelte:head>
	<title>Real Estate Videos, Podcasts & Blog | Fergmar Capital</title>
	<meta
		name="description"
		content="Watch videos, listen to podcasts, and read articles from the Fergmar Capital team on real estate syndication, passive investing strategies, and wealth building."
	/>
	<meta property="og:title" content="Real Estate Videos, Podcasts & Blog | Fergmar Capital" />
	<meta
		property="og:description"
		content="Videos, podcasts, and articles on real estate syndication, passive investing strategies, and wealth building from Fergmar Capital."
	/>
	<meta name="twitter:title" content="Real Estate Videos, Podcasts & Blog | Fergmar Capital" />
	<meta
		name="twitter:description"
		content="Videos, podcasts, and articles on real estate syndication, passive investing strategies, and wealth building."
	/>

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: "Fergmar Capital Media",
		description:
			"Videos, podcasts, and articles on real estate syndication, passive investing strategies, and wealth building from Fergmar Capital.",
		url: "https://fergmarcapital.com/media",
		mainEntity: {
			"@type": "ItemList",
			itemListElement: [
				{
					"@type": "ListItem",
					position: 1,
					name: "YouTube Videos",
					url: "https://fergmarcapital.com/media/youtube"
				},
				{
					"@type": "ListItem",
					position: 2,
					name: "Podcast Episodes",
					url: "https://fergmarcapital.com/media/podcasts"
				},
				{
					"@type": "ListItem",
					position: 3,
					name: "Blog Articles",
					url: "https://fergmarcapital.com/media/blog"
				}
			]
		}
	})}</script>`}
</svelte:head>

<h1 class="sr-only">Real Estate Investing Videos, Podcasts & Articles</h1>

<YouTubeSection
	label="Video Insights"
	title="See Our Strategy in Action"
	subtitle="Watch our team break down deal analysis, portfolio performance, and market offerings firsthand."
	videos={data.youtubeVideos.slice(0, 6)}
	theme="light"
>
	{#snippet footer()}
		<div class="mt-10 text-center">
			<a
				href="/media/youtube"
				class="text-foreground hover:text-foreground/80 border-foreground/20 hover:border-foreground/40 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors"
			>
				View all videos
				<ArrowRightIcon class="h-3.5 w-3.5" />
			</a>
		</div>
	{/snippet}
</YouTubeSection>

<PodcastSection
	label="Podcast Features"
	title="Hear From Our Team"
	subtitle="Brian Ferguson joins leading real estate podcasts to share hands-on strategies for building wealth through commercial real estate."
	episodes={data.podcastEpisodes.slice(0, 6)}
	theme="dark"
>
	{#snippet footer()}
		<div class="mt-10 text-center">
			<a
				href="/media/podcasts"
				class="text-foreground hover:text-foreground/80 border-foreground/20 hover:border-foreground/40 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors"
			>
				View all podcasts
				<ArrowRightIcon class="h-3.5 w-3.5" />
			</a>
		</div>
	{/snippet}
</PodcastSection>

<BlogSection
	label="Market Intelligence"
	title="Insights & Analysis"
	subtitle="In-depth analysis on market trends, investment strategies, and lessons from our portfolio operations."
	posts={data.blogPosts.slice(0, 6)}
	blogBaseUrl="/media/blog"
	theme="muted"
>
	{#snippet footer()}
		<div class="mt-10 text-center">
			<a
				href="/media/blog"
				class="text-foreground hover:text-foreground/80 border-foreground/20 hover:border-foreground/40 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors"
			>
				View all articles
				<ArrowRightIcon class="h-3.5 w-3.5" />
			</a>
		</div>
	{/snippet}
</BlogSection>
